/*
 * <route-map> — the Route Pick-Up page's map and stop list (sections/swrfm-routes.liquid).
 *
 * William, 2026-09-29: "how to show in a map the regions over miami". The page listed its 15 stops
 * by hand in the theme editor, so a stop moved in the app (Operations → Routes & stops) stayed
 * wrong on the page until somebody retyped it. This reads the same book the cart's chooser reads
 * (GET /api/storefront/pickup-options): stops, times, meeting points, coordinates, next dates.
 *
 * The map is Leaflet (assets/leaflet.js, BSD-2) over OpenStreetMap tiles: no key, no account. It
 * loads only when the section is about to be seen. The LIST is the content; the map is a way into
 * it, so a browser that cannot draw the map still has every stop.
 */
(function () {
  if (customElements.get('route-map')) return;

  var OPTIONS_TTL_MS = 60 * 1000;
  var OPTIONS_TIMEOUT_MS = 8000;
  // One colour per route, in the order the book lists them; the market itself is the dark pin.
  var PALETTE = ['#5a653d', '#b4602a', '#2c6e73', '#7b4a86', '#9a7b1f', '#3d5a8a'];
  var MARKET = '#212020';
  var TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  var ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" rel="noopener" target="_blank">OpenStreetMap</a> contributors';

  function wait(ms) { return new Promise(function (done) { setTimeout(done, ms); }); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) { return '$' + (cents / 100).toFixed(2); }

  function getJson(url) {
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctl ? setTimeout(function () { ctl.abort(); }, OPTIONS_TIMEOUT_MS) : null;
    return fetch(url, { credentials: 'omit', signal: ctl ? ctl.signal : undefined }).then(function (r) {
      if (!r.ok) throw new Error('status ' + r.status);
      return r.json();
    }).then(function (body) {
      clearTimeout(timer);
      if (!body || !Array.isArray(body.routes)) throw new Error('not pickup options');
      return body;
    }, function (e) { clearTimeout(timer); throw e; });
  }
  // The chooser's two roads and its cache (component-pickup-chooser.js): the app's own address,
  // then the store's app proxy, then the first once more.
  function fetchOptions(endpoint, proxy) {
    var cache = window.__swrfmPickupOptions;
    if (cache && Date.now() - cache.at < OPTIONS_TTL_MS) return cache.promise;
    var promise = getJson(endpoint).catch(function (first) {
      var why = encodeURIComponent(String((first && (first.name + ' ' + first.message)) || first).slice(0, 80));
      function again() { return wait(1500).then(function () { return getJson(endpoint); }); }
      if (!proxy) return again();
      return getJson(proxy + (proxy.indexOf('?') === -1 ? '?' : '&') + 'via=store&why=' + why).catch(again);
    });
    window.__swrfmPickupOptions = { at: Date.now(), promise: promise };
    promise.catch(function () { window.__swrfmPickupOptions = null; });
    return promise;
  }

  function loadLeaflet(js, css) {
    if (window.L && window.L.map) return Promise.resolve(window.L);
    if (window.__swrfmLeaflet) return window.__swrfmLeaflet;
    window.__swrfmLeaflet = new Promise(function (done, fail) {
      var link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = css;
      document.head.appendChild(link);
      var s = document.createElement('script');
      s.src = js; s.async = true;
      s.onload = function () { window.L ? done(window.L) : fail(new Error('no L')); };
      s.onerror = function () { fail(new Error('leaflet')); };
      document.head.appendChild(s);
    });
    window.__swrfmLeaflet.catch(function () { window.__swrfmLeaflet = null; });
    return window.__swrfmLeaflet;
  }

  function miles(a, b) {
    var R = 3958.8, rad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }
  function dayWords(route) {
    if (route.schedule === 'first-saturday') return 'First Saturday of the month';
    return route.weekday ? route.weekday + 's' : '';
  }
  function addressLine(a) {
    if (!a) return '';
    var region = a.region === 'Florida' ? 'FL' : a.region;
    return [a.line1, [a.city, [region, a.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', ');
  }
  function directions(stop) {
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(addressLine(stop.address));
  }
  // The book says it on every stop ("… We will only stay for 15 minutes."); the page says it once,
  // in the notice, so the card keeps only where to meet.
  function meetingPoint(notes) {
    return String(notes || '').replace(/\s*We will (only )?(be at this location at [^.]*\.\s*We will only )?stay for 15 minutes\.?/gi, '').trim();
  }

  class RouteMap extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      try { this.fees = JSON.parse(this.dataset.fees || '{}'); } catch (e) { this.fees = {}; }
      this.filter = 'all';
      this.day = 'all';
      this.innerHTML = '<div class="swr-routes__loading">Loading the route stops…</div>';
      var self = this;
      fetchOptions(this.dataset.endpoint, this.dataset.proxy).then(function (opts) {
        self.prepare(opts);
        self.render();
        self.whenSeen();
      }).catch(function () { self.failed(); });
    }

    failed() {
      this.innerHTML = '<div class="swr-routes__loading">We could not load the route stops. ' +
        '<button type="button" class="swr-routes__link" data-act="retry">Try again</button>, or call (954) 854-4185.</div>';
      var self = this;
      this.querySelector('[data-act="retry"]').addEventListener('click', function () { self.started = false; self.connectedCallback(); });
    }

    prepare(opts) {
      var self = this;
      this.market = null;
      this.routes = [];
      opts.routes.forEach(function (r) {
        if (r.kind === 'in-store') { if (r.stops[0]) self.market = r.stops[0]; return; }
        var color = PALETTE[self.routes.length % PALETTE.length];
        var fee = r.schedule === 'first-saturday' ? self.fees['Naples Route'] : self.fees['Route'];
        self.routes.push({
          id: r.id, label: r.label, color: color, day: dayWords(r), fee: fee ? fee.price : null, monthly: r.schedule === 'first-saturday',
          stops: r.stops.filter(function (s) { return typeof s.lat === 'number' && typeof s.lng === 'number'; })
            .map(function (s, i) { return { route: r.id, n: i + 1, id: s.id, label: s.label, time: s.time, address: s.address, lat: s.lat, lng: s.lng, where: meetingPoint(s.notes), dates: s.dates || [] }; }),
        });
      });
      this.deadline = opts.deadline && opts.deadline.text;
    }

    stops() { return this.routes.reduce(function (all, r) { return all.concat(r.stops); }, []); }
    route(id) { return this.routes.filter(function (r) { return r.id === id; })[0]; }
    // The day a route runs, as the day chips name it: 'friday' or 'saturday' (Naples is a Saturday).
    dayOf(r) { return /^friday/i.test(r.day) ? 'friday' : 'saturday'; }
    // Which routes are showing: the day chosen first, then the route chosen inside it.
    visible(r) { return (this.day === 'all' || this.dayOf(r) === this.day) && (this.filter === 'all' || this.filter === r.id); }
    visibleStops() { var self = this; return this.routes.filter(function (r) { return self.visible(r); }).reduce(function (all, r) { return all.concat(r.stops); }, []); }

    render() {
      var self = this;
      var total = this.stops().length;
      // The day chips (William, 2026-09-30: "add the friday and saturday as buttons"): a customer
      // knows which day they are free before they know a route's name. A day narrows the route
      // chips, the list and the map; "Any day" is the whole picture.
      var counts = { friday: 0, saturday: 0 };
      this.routes.forEach(function (r) { counts[self.dayOf(r)] += r.stops.length; });
      var days = [['all', 'Any day', total], ['friday', 'Fridays', counts.friday], ['saturday', 'Saturdays', counts.saturday]];
      var html = '<div class="swr-routes__days" role="group" aria-label="Pickup day"><span class="swr-routes__days-label">Pickup day</span>';
      days.forEach(function (d) {
        var on = self.day === d[0];
        html += '<button type="button" class="swr-routes__chip swr-routes__chip--day' + (on ? ' is-on' : '') + '" data-act="day" data-day="' + d[0] + '" aria-pressed="' + on + '">' + d[1] + ' <span class="swr-routes__chip-day">' + d[2] + ' stops</span></button>';
      });
      html += '</div>';
      var inDay = this.routes.filter(function (r) { return self.day === 'all' || self.dayOf(r) === self.day; });
      var inDayStops = inDay.reduce(function (n, r) { return n + r.stops.length; }, 0);
      html += '<div class="swr-routes__bar" role="group" aria-label="Show a route">' +
        '<button type="button" class="swr-routes__chip' + (this.filter === 'all' ? ' is-on' : '') + '" data-act="filter" data-route="all" aria-pressed="' + (this.filter === 'all') + '">' + (this.day === 'all' ? 'All ' + total + ' stops' : 'All ' + inDayStops + ' ' + (this.day === 'friday' ? 'Friday' : 'Saturday') + ' stops') + '</button>';
      inDay.forEach(function (r) {
        var on = self.filter === r.id;
        html += '<button type="button" class="swr-routes__chip' + (on ? ' is-on' : '') + '" data-act="filter" data-route="' + esc(r.id) + '" aria-pressed="' + on + '" style="--swr-route:' + r.color + '">' +
          '<span class="swr-routes__dot"></span>' + esc(r.label.replace(/\s+Routes?$/i, '')) + ' <span class="swr-routes__chip-day">' + esc(r.day.replace(' of the month', '')) + '</span></button>';
      });
      html += '<button type="button" class="swr-routes__near" data-act="near">' +
        '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M12 2 4.5 20.3l.7.7L12 18l6.8 3 .7-.7z"/></svg> Find the stop nearest me</button></div>' +
        '<div class="swr-routes__near-msg" role="status" aria-live="polite"></div>';

      html += '<div class="swr-routes__body">' +
        '<div class="swr-routes__map-wrap"><div class="swr-routes__map" role="application" aria-label="Map of the route pickup stops across South Florida"></div></div>' +
        '<div class="swr-routes__list">';
      this.routes.forEach(function (r) {
        if (!self.visible(r)) return;
        html += '<section class="swr-routes__group" style="--swr-route:' + r.color + '" aria-labelledby="swr-r-' + esc(r.id) + '">' +
          '<header class="swr-routes__group-head"><h3 id="swr-r-' + esc(r.id) + '" class="swr-routes__group-title"><span class="swr-routes__dot"></span>' + esc(r.label) + '</h3>' +
          '<p class="swr-routes__group-sub">' + esc(r.day) + (r.fee != null ? ' · ' + money(r.fee) + ' route fee' : '') + '</p></header><ol class="swr-routes__stops">';
        r.stops.forEach(function (s) {
          var next = s.dates.map(function (d) { return d.label; }).join(' · ');
          html += '<li class="swr-routes__stop" data-stop="' + esc(s.id) + '" tabindex="0">' +
            '<span class="swr-routes__pin" aria-hidden="true">' + s.n + '</span>' +
            '<div class="swr-routes__stop-main">' +
              '<div class="swr-routes__stop-top"><span class="swr-routes__stop-name">' + esc(s.label) + '</span><span class="swr-routes__stop-time">' + esc(s.time || '') + '</span></div>' +
              '<a class="swr-routes__stop-addr" href="' + esc(directions(s)) + '" target="_blank" rel="noopener">' + esc(addressLine(s.address)) + '</a>' +
              (s.where ? '<div class="swr-routes__stop-where">' + esc(s.where) + '</div>' : '') +
              (next ? '<div class="swr-routes__stop-next">Next: ' + esc(next) + '</div>' : '') +
              '<div class="swr-routes__stop-dist"></div>' +
            '</div></li>';
        });
        html += '</ol></section>';
      });
      html += '</div></div>';
      if (this.deadline) html += '<p class="swr-routes__deadline">' + esc(this.deadline) + ' for that week’s pickup.</p>';
      this.innerHTML = html;
      this.map = null;
      this.bind();
      if (this.seen) this.draw();
      if (this.me) this.showDistances();
    }

    bind() {
      var self = this;
      this.querySelectorAll('[data-act="filter"]').forEach(function (b) {
        b.addEventListener('click', function () { self.filter = b.dataset.route; self.render(); });
      });
      // A day chosen opens on all of that day's routes; the route chips narrow within it.
      this.querySelectorAll('[data-act="day"]').forEach(function (b) {
        b.addEventListener('click', function () { self.day = b.dataset.day; self.filter = 'all'; self.render(); });
      });
      this.querySelector('[data-act="near"]').addEventListener('click', function () { self.nearest(); });
      this.querySelectorAll('.swr-routes__stop').forEach(function (li) {
        function open(e) {
          if (e.target.closest('a')) return;
          if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
          if (e.type === 'keydown') e.preventDefault();
          self.focusStop(li.dataset.stop, true);
        }
        li.addEventListener('click', open);
        li.addEventListener('keydown', open);
      });
    }

    whenSeen() {
      var self = this;
      if (!('IntersectionObserver' in window)) { this.seen = true; this.draw(); return; }
      var io = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        io.disconnect();
        self.seen = true;
        self.draw();
      }, { rootMargin: '400px 0px' });
      io.observe(this);
    }

    draw() {
      var self = this;
      var box = this.querySelector('.swr-routes__map');
      if (!box || this.map) return;
      loadLeaflet(this.dataset.leafletJs, this.dataset.leafletCss).then(function (L) {
        if (!box.isConnected || self.map) return;
        // A page that scrolls must keep scrolling over the map: the wheel zooms only after a click.
        var map = L.map(box, { scrollWheelZoom: false, zoomControl: true, attributionControl: true });
        map.on('click', function () { map.scrollWheelZoom.enable(); });
        box.addEventListener('mouseleave', function () { map.scrollWheelZoom.disable(); });
        L.tileLayer(TILES, { maxZoom: 18, minZoom: 7, attribution: ATTRIBUTION }).addTo(map);
        self.map = map;
        self.markers = {};
        // What the map opens on. With every route shown that is the WEEKLY stops and the market:
        // Naples is a coast away and once a month, and fitting it in shrinks the fifteen stops a
        // customer is looking for into one smudge. Its chip goes there.
        var shown = [];
        self.routes.forEach(function (r) {
          if (!self.visible(r)) return;
          var pts = r.stops.map(function (s) { return [s.lat, s.lng]; });
          // The ground a route covers: the van's own path from stop to stop, drawn wide. (The
          // outline of the stops was tried first and read as a wedge across Biscayne Bay.) One
          // stop, Naples, is a circle.
          if (pts.length >= 2) {
            L.polyline(pts, { color: r.color, weight: 46, opacity: .34, lineCap: 'round', lineJoin: 'round', interactive: false }).addTo(map);
            L.polyline(pts, { color: '#fff', weight: 5, opacity: .9, lineCap: 'round', interactive: false }).addTo(map);
            L.polyline(pts, { color: r.color, weight: 3, opacity: 1, dashArray: '1 7', lineCap: 'round', interactive: false }).addTo(map);
          } else if (pts.length === 1) L.circle(pts[0], { radius: 9000, color: r.color, weight: 0, fillColor: r.color, fillOpacity: .34, interactive: false }).addTo(map);
          var opens = self.filter === r.id || (self.filter === 'all' && r.monthly !== true);
          r.stops.forEach(function (s) {
            var m = L.marker([s.lat, s.lng], {
              title: s.label + (s.time ? ' · ' + s.time : ''), keyboard: false,
              icon: L.divIcon({ className: 'swr-routes__marker', html: '<span style="--swr-route:' + r.color + '">' + s.n + '</span>', iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -14] }),
            }).addTo(map);
            m.bindPopup(
              '<div class="swr-routes__pop"><strong>' + esc(s.label) + '</strong>' +
              '<div>' + esc([r.day.replace(' of the month', ''), s.time].filter(Boolean).join(' · ')) + '</div>' +
              '<div class="swr-routes__pop-addr">' + esc(addressLine(s.address)) + '</div>' +
              (s.where ? '<div>' + esc(s.where) + '</div>' : '') +
              '<a href="' + esc(directions(s)) + '" target="_blank" rel="noopener">Directions</a></div>', { maxWidth: 240 });
            m.on('click', function () { self.focusStop(s.id, false); });
            self.markers[s.id] = m;
            if (opens) shown.push([s.lat, s.lng]);
          });
        });
        if (self.market && typeof self.market.lat === 'number') {
          L.marker([self.market.lat, self.market.lng], {
            title: 'Southwest Ranches Farmers Market', keyboard: false, zIndexOffset: -100,
            icon: L.divIcon({ className: 'swr-routes__marker swr-routes__marker--market', html: '<span style="--swr-route:' + MARKET + '">★</span>', iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -14] }),
          }).addTo(map).bindPopup('<div class="swr-routes__pop"><strong>Southwest Ranches Farmers Market</strong><div>In-store pickup</div><div class="swr-routes__pop-addr">' + esc(addressLine(self.market.address)) + '</div></div>', { maxWidth: 240 });
          if (self.filter === 'all') shown.push([self.market.lat, self.market.lng]);
        }
        if (shown.length) map.fitBounds(shown, { padding: [44, 44], maxZoom: 12 });
        if (self.me) self.markMe();
      }).catch(function () {
        var wrap = self.querySelector('.swr-routes__map-wrap');
        if (wrap) wrap.hidden = true; // the list stands on its own
      });
    }

    focusStop(id, move) {
      this.querySelectorAll('.swr-routes__stop').forEach(function (li) { li.classList.toggle('is-on', li.dataset.stop === id); });
      var m = this.markers && this.markers[id];
      if (m && this.map) {
        if (move) this.map.setView(m.getLatLng(), Math.max(this.map.getZoom(), 12), { animate: true });
        m.openPopup();
      }
      if (!move) {
        var li = this.querySelector('.swr-routes__stop[data-stop="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]');
        var list = this.querySelector('.swr-routes__list');
        // Inside the list's own scroll on a laptop; never the page, which would leave the map.
        if (li && list && list.scrollHeight > list.clientHeight) list.scrollTo({ top: li.offsetTop - list.offsetTop - 12, behavior: 'smooth' });
      }
    }

    nearest() {
      var self = this;
      var msg = this.querySelector('.swr-routes__near-msg');
      if (!navigator.geolocation) { msg.textContent = 'This browser cannot share a location. Choose a route above.'; return; }
      msg.textContent = 'Finding you…';
      navigator.geolocation.getCurrentPosition(function (pos) {
        self.me = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        self.filter = 'all';
        self.render();
      }, function () {
        msg.textContent = 'We could not read your location. Choose a route above, or tap a stop on the map.';
      }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 });
    }

    showDistances() {
      var self = this, best = null;
      this.visibleStops().forEach(function (s) {
        var d = miles(self.me, s);
        var el = self.querySelector('.swr-routes__stop[data-stop="' + (window.CSS && CSS.escape ? CSS.escape(s.id) : s.id) + '"] .swr-routes__stop-dist');
        if (el) el.textContent = d.toFixed(d < 10 ? 1 : 0) + ' mi from you';
        if (!best || d < best.d) best = { d: d, s: s };
      });
      var msg = this.querySelector('.swr-routes__near-msg');
      if (!best || !msg) return;
      var r = this.route(best.s.route);
      // Florida is long: past 60 miles from every stop, "nearest" is not an offer.
      msg.innerHTML = best.d > 60
        ? 'Our closest stop to you is <strong>' + esc(best.s.label) + '</strong>, ' + best.d.toFixed(0) + ' miles away.'
        : 'Nearest to you: <strong>' + esc(best.s.label) + '</strong> · ' + esc(r.day.replace(' of the month', '')) + ' ' + esc(best.s.time || '') + ' · ' + best.d.toFixed(1) + ' mi';
      this.nearestId = best.s.id;
      var li = this.querySelector('.swr-routes__stop[data-stop="' + (window.CSS && CSS.escape ? CSS.escape(best.s.id) : best.s.id) + '"]');
      if (li) li.classList.add('is-on', 'is-nearest');
    }

    markMe() {
      var L = window.L;
      L.circleMarker([this.me.lat, this.me.lng], { radius: 7, color: '#fff', weight: 3, fillColor: '#1a73e8', fillOpacity: 1, interactive: false }).addTo(this.map);
      var m = this.nearestId && this.markers[this.nearestId];
      if (m) { this.map.fitBounds([[this.me.lat, this.me.lng], m.getLatLng()], { padding: [60, 60], maxZoom: 13 }); m.openPopup(); }
    }
  }

  customElements.define('route-map', RouteMap);
})();
